import { useEffect, useMemo, useRef, useState } from 'react';
import { sideLights, type BoardRunView, type HiLoApi, type SlotDemoScene } from '@goodtime/engine';
import type { HiLoOffer, RoadRestoreResponse, RoadState } from '@goodtime/shared';
import { funnelBeacon } from '../../api/funnel';
import { actPlay, declinePlay, getPlay } from '../../api/plays';
import { withRetry } from '../../api/road';
import { boardStatus } from './boardText';
import { slotChromeLayout } from './slotChromeLayout';
import { useLandmarkRoad } from './useLandmarkRoad';

/**
 * Everything Royal Road puts round the slot, mounted once by App for a
 * flagged player on a road slot. It draws nothing itself: the panel, the
 * run bar and the Hi-Lo bonus are drawn by the slot scene on its canvas
 * (producer 2026-10-14), so this works out where they go, keeps the scene
 * up to date and plays the bonus.
 */
export function SlotJourneyChrome({
  road,
  scene,
  spinKey,
  safeTop,
  hidden = false,
  view,
  notice,
  bonus,
  sessionId,
  playerId,
  onBonusDone,
  themeId = null,
  unlockedSlotIds,
  roadOn,
  onRestore,
  onSlotOpened,
  onPlaySlot,
}: {
  road: RoadState | null;
  scene: SlotDemoScene | null;
  spinKey: number;
  safeTop: number;
  hidden?: boolean;
  /** The Wheel Run, while one is on (phase idle otherwise). */
  view: BoardRunView;
  notice: string | null;
  /** A Hi-Lo bonus offered after a collect. */
  bonus: HiLoOffer | null;
  sessionId: string | null;
  playerId?: string | null;
  onBonusDone: (payout: number | null) => void;
  /** Phase 2a: the slot; restoring a landmark on the road under the SPIN
   *  row; the slot a finished chapter opened, and going to play it. */
  themeId?: string | null;
  /** The slots the player has opened, so the cards don't promise one they
   *  have; and whether a slot has a road for them. */
  unlockedSlotIds?: readonly string[];
  roadOn?: (themeId: string) => boolean;
  onRestore?: (landmark: number) => Promise<RoadRestoreResponse>;
  onSlotOpened?: (slotId: string) => void;
  onPlaySlot?: (slotId: string) => void;
}) {
  const [viewport, setViewport] = useState(() => ({ w: window.innerWidth, h: window.innerHeight }));
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const layout = useMemo(() => slotChromeLayout(viewport.w, viewport.h, safeTop), [viewport.w, viewport.h, safeTop]);

  // Room for the panel is reserved straight away, so the reels don't move
  // when the road arrives.
  useEffect(() => {
    scene?.setChromeInsets(layout.insets);
    scene?.setRoadChrome({
      mode: layout.mode,
      top: layout.panel.top,
      height: layout.panel.height,
      // Between the top bar's corners the panel can't grow past its room.
      maxWidth: layout.beside ? layout.panel.width : 640,
    });
  }, [scene, layout]);

  useEffect(() => {
    if (!scene) return;
    scene.setRoadState(road, spinKey);
    if (!road) return;
    const lights = sideLights(road);
    scene.setSideLights({ lit: lights.lit, next: lights.next });
  }, [scene, road, spinKey]);

  const lamps = road?.lamps;
  // The slot names a Wheel Run's lamp games; it changes only with the road
  // (and a new scene), so it's read, not watched: a slot change must not
  // reach the last slot's scene.
  const theme = useRef(themeId);
  theme.current = themeId;
  useEffect(() => {
    scene?.setRunInfo(view.phase === 'idle' ? null : view, notice ?? boardStatus(view, lamps, theme.current), notice !== null);
  }, [scene, view, notice, lamps]);

  useEffect(() => {
    scene?.setRoadChromeHidden(hidden);
  }, [scene, hidden]);

  // The road's landmarks, drawn by the scene under the SPIN row.
  useLandmarkRoad({
    scene,
    road: hidden ? null : road,
    themeId,
    playerId,
    unlockedSlotIds,
    roadOn,
    onRestore,
    onSlotOpened,
    onPlaySlot,
  });

  // The Hi-Lo bonus, once per offer.
  const played = useRef<string | null>(null);
  const done = useRef(onBonusDone);
  done.current = onBonusDone;
  useEffect(() => {
    if (!scene || !bonus || !sessionId || played.current === bonus.playId) return;
    played.current = bonus.playId;
    const playId = bonus.playId;
    const api: HiLoApi = {
      act: (step, action, arg) => withRetry(() => actPlay(playId, { sessionId, step, action, arg })),
      decline: () => declinePlay(playId, sessionId).then(() => undefined),
      recover: async () => {
        const play = await getPlay(playId, sessionId).catch(() => null);
        if (!play) return null;
        return { step: play.step, state: play.state, finished: play.status === 'done' || play.status === 'expired', payout: play.payout };
      },
      beacon: (event) => funnelBeacon(event, { playerId, detail: String(bonus.base) }),
    };
    void scene.playHiLo(bonus, api).then((payout) => done.current(payout));
  }, [scene, bonus, sessionId, playerId]);

  return null;
}
